import { math } from 'polished';
import { SDS_SIZE_BORDER_SMALL } from '@s-group/design-system-tokens/web/tokens/size.es6.js';

/**
 * Subtracts the border width from the padding.
 *
 * In CSS box model borders are rendered outside padding area. This function
 * subtracts the border width from the space token used for the padding to keep
 * the dimensions of the bordered element same as the unbordered one.
 *
 * @param paddingSize unbordered element padding with a unit
 * @param borderSize element border width, defaults to SDS_SIZE_BORDER_SMALL
 * @returns bordered element padding
 */
function bordered(paddingSize, borderSize = SDS_SIZE_BORDER_SMALL) {
    return math(`${paddingSize} - ${borderSize}`);
}
/**
 * Determines if browser DOM is available
 *
 * @returns true if browser is used
 */
function isBrowser() {
    return !!(typeof window !== 'undefined' &&
        window.document &&
        window.document.createElement);
}
/**
 * Determines if server-side rendering is used.
 *
 * @returns true in server-side rendering is used.
 */
function isSsr() {
    return !isBrowser();
}
/**
 * Converts a length measurement in rem units to pixels.
 *
 * This function is mainly intended for converting size token values to units
 * usable in SVG coordinate space.
 *
 * @param remValue value in rem units
 * @param pxPerRem pixels per rem scaling factor, defaults to 16
 * @returns value in pixels
 */
function pxValue(remValue, pxPerRem = 16) {
    const rem = typeof remValue === 'string' ? parseFloat(remValue) : remValue;
    return rem * pxPerRem;
}
/**
 * Calculates a scaled icon size relative to a font size and a negative margin
 * to compensate the whitespace within the scaled icon asset in relation to
 * font line-height.
 *
 * @param fontMetric A tuple of `[fontSize, lineHeight]`
 * @param iconAssetInitialSize Icon asset initial size in px (optional)
 * @param pxPerRem Custom px/rem ratio (optional)
 * @returns An object with `iconSize` and `iconMargin` properties.
 */
const scaledIcon = (fontMetric, iconAssetInitialSize = '24', pxPerRem = 16) => {
    const [fontSize, lineHeight] = fontMetric;
    const iconSize = math(`${fontSize} * ${iconAssetInitialSize} / ${pxPerRem}`);
    const iconMargin = math(`(${lineHeight} - ${iconSize}) / 2`);
    return { iconSize, iconMargin };
};

export { bordered, isBrowser, isSsr, pxValue, scaledIcon };
//# sourceMappingURL=util.js.map
